import { useMemo } from 'react';
import {
  Paper,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import { PaginatedClientResponseModelAccessRequestClientModel } from '@client-app-sdk/api/v1';

import { TableColWrapper, TableWrapper } from '@common/ui/TableWrapper';
import { Status } from '@common/ui/Status';
import { PagedPagination } from '@common/ui/PagedPagination';
import { TableLoading } from '@common/ui/TableLoading';

import IntegrationsCell from '@common/components/IntegrationsCell';
import ResourceTypesCell from '@common/components/ResourceTypesCell';
import ChallengeCell from '@common/components/ChalengesCell';
import { AnalyticsEvents } from '@common/utils/analytics/events';
import RevocationTimeCell from '../components/RevocationTimeCell';
import { DateTime } from '@common/utils/datetime';
import PrimarySecondary from '@common/ui/PrimarySecondary';

export default function OpenRequestsTable({
  isLoading,
  pageData: { data, pagination },
  selectedRequestId,
  handleRequestClick,
  handleOnPageChange,
}: {
  isLoading: boolean;
  pageData: PaginatedClientResponseModelAccessRequestClientModel;
  selectedRequestId?: string;
  handleRequestClick: (requestId: string) => void;
  handleOnPageChange: (page: number) => void;
}) {
  return useMemo(
    () => (
      <Stack direction="column" justifyContent="center" alignItems="stretch" spacing={2}>
        <TableWrapper>
          <TableLoading loading={isLoading} />
          <TableContainer component={Paper} elevation={0}>
            <MuiTable>
              <TableHead>
                <TableRow>
                  <TableCell component="th" size="medium">
                    <TableColWrapper>ID</TableColWrapper>
                  </TableCell>
                  <TableCell component="th" size="medium">
                    <TableColWrapper>Integrations</TableColWrapper>
                  </TableCell>
                  <TableCell component="th" size="medium">
                    <TableColWrapper>Resource Types</TableColWrapper>
                  </TableCell>
                  <TableCell component="th" size="medium">
                    <TableColWrapper>Approvals</TableColWrapper>
                  </TableCell>
                  <TableCell component="th" size="medium">
                    <TableColWrapper>Status</TableColWrapper>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((request) => (
                  <TableRow
                    data-trigger={AnalyticsEvents.OpenRequestClicked}
                    key={request.id}
                    hover
                    className="MuiTableRow-clickable"
                    onClick={() => handleRequestClick(request.id)}
                    selected={selectedRequestId === request.id}
                  >
                    <TableCell component="td">
                      <PrimarySecondary
                        primary={request.id}
                        secondary={DateTime.fromUnix(Number(request.creation_time)).readable}
                      />
                    </TableCell>
                    <TableCell component="td">
                      <IntegrationsCell
                        integrations={request.access_groups.map((ag) => ag.integration).flat()}
                        bundleName={request.bundle?.name}
                      />
                    </TableCell>
                    <TableCell component="td">
                      <ResourceTypesCell resourceTypes={request.access_groups.map((ag) => ag.resource_types).flat()} />
                    </TableCell>
                    <TableCell component="td">
                      <ChallengeCell challenge={request.challenge} />
                    </TableCell>
                    <TableCell component="td">
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Status status={request.status.status} description={request.status.description} />
                        <RevocationTimeCell revocation_time={request.revocation_time} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
        </TableWrapper>
        <PagedPagination isLoading={isLoading} pagination={pagination} onChange={handleOnPageChange} />
      </Stack>
    ),
    [isLoading, data, pagination, handleOnPageChange, selectedRequestId, handleRequestClick],
  );
}
